import React, { useMemo } from "react";
import { X } from "lucide-react";
import FullscreenViewer from "ui/FullscreenViewer";
import MediaCarousel from "ui/MediaCarousel";
import EntityActions from "components/Entities/EntityActions";
import type { Entity } from "util/constants";
import type { ExistingMediaFile } from "util/mediaTypes";
import useMobileViewport from "hooks/useMobileViewport";

interface MediaViewerProps {
  entity: Entity;
  entityType: string;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Optional header content (avatar + more-options menu) rendered as the
   * bottom-overlay strip above the action rail. Mirrors the entity card's
   * own header without re-implementing avatar, ownership, or menu logic
   * inside the viewer.
   */
  renderHeader?: () => React.ReactNode;
}

const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN || "";

const isExistingMediaFile = (value: unknown): value is ExistingMediaFile => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as { key?: unknown; type?: unknown };
  return (
    typeof candidate.key === "string" &&
    (candidate.type === "image" || candidate.type === "video")
  );
};

const extractMediaFiles = (entity: Entity): ExistingMediaFile[] => {
  const props = entity.properties;
  if (!props || typeof props !== "object") return [];
  const candidate = (props as { media_files?: unknown }).media_files;
  if (!Array.isArray(candidate)) return [];
  return candidate.filter(isExistingMediaFile);
};

/**
 * Full-screen media viewer for a single entity.
 *
 * Layout (Instagram Reels-inspired):
 * - Solid-black backdrop, no extra chrome that competes with the media.
 * - Close (X) button: top-left on mobile, top-right on desktop.
 * - Vertical action rail along the right edge (Vote, Award, Comment, Share,
 *   Save). Background is fully transparent so the media reads through.
 * - Bottom strip: Avatar + entity context + more-options menu, rendered
 *   above the video controls (and any in-video progress bar).
 *
 * Click target rules (delegated to MediaCarousel + VideoPlayer):
 * - Tapping a video toggles play/pause; only the explicit fullscreen button
 *   in the video controls enters native fullscreen.
 * - Carousel video state (currentTime/playbackRate/volume/userPaused) is
 *   shared with the originating card via `videoPlayerStateStore`, so the
 *   transition into the viewer is seamless.
 */
const MediaViewer: React.FC<MediaViewerProps> = ({
  entity,
  entityType,
  isOpen,
  onClose,
  renderHeader,
}) => {
  const isMobileViewport = useMobileViewport();
  const mediaFiles = useMemo(() => extractMediaFiles(entity), [entity]);
  const sortedMedia = useMemo(
    () => [...mediaFiles].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [mediaFiles],
  );

  if (!isOpen || sortedMedia.length === 0) {
    return null;
  }

  return (
    <FullscreenViewer
      isOpen={isOpen}
      onClose={onClose}
      closeButtonPosition={isMobileViewport ? "left" : "right"}
      hideHeader={true}
      className="bg-black dark:bg-black"
    >
      {/* Close button overlay. Top-left on mobile, top-right on desktop. */}
      <div
        className={`media-viewer-overlay pointer-events-none absolute top-0 inset-x-0 z-[60] flex items-start px-3 pt-3 pb-2 ${
          isMobileViewport ? "justify-start" : "justify-end"
        }`}
      >
        <button
          type="button"
          className="pointer-events-auto flex size-10 items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
          aria-label="Close"
          onClick={onClose}
        >
          <X className="size-6" aria-hidden />
        </button>
      </div>

      {/* Media surface. Full viewport. */}
      <div className="relative w-full h-full flex items-stretch justify-center">
        <MediaCarousel
          mediaFiles={sortedMedia}
          imageCdn={IMAGE_CDN}
          className="w-full h-full max-h-full flex items-center justify-center"
          showDots={sortedMedia.length > 1}
          showArrows={sortedMedia.length > 1}
          allowFullHeight={true}
        />

        {/* Right-edge vertical action rail. Transparent background to keep
            the media legible (Instagram-style). The rail is anchored above
            the video controls / progress bar so its buttons never overlap
            in-video controls (timestamp, fullscreen, etc). */}
        <div className="media-viewer-overlay pointer-events-none absolute right-1 bottom-24 sm:bottom-28 z-20 flex flex-col items-end px-1 select-none">
          <div className="pointer-events-auto dark text-white">
            <EntityActions
              entity={entity as any}
              entityType={entityType}
              vertical
            />
          </div>
        </div>

        {/* Bottom strip: avatar + meta + more-options menu. Sits above the
            video's own controls (which occupy ~64px at the bottom). The
            right padding keeps the avatar/meta content from running into
            the right-edge action rail. */}
        {renderHeader ? (
          <div className="media-viewer-overlay pointer-events-none absolute inset-x-0 bottom-12 sm:bottom-16 z-10 px-3 pt-10 pb-2 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
            <div className="pointer-events-auto dark text-white pr-16 sm:pr-20">
              {renderHeader()}
            </div>
          </div>
        ) : null}
      </div>
    </FullscreenViewer>
  );
};

export default MediaViewer;
